import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { safeIsoString } from '@/utils/appointmentCivilDate';
import { clinicAddDays, clinicStartOfDay } from '@/utils/calendarClinicTime';
import type { BlockTargetOccupant } from '@/utils/multiBlockOverlap';
import type { SlotOccupant } from '@/utils/appointmentSlotHint';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type OccupantRow = {
  id: string;
  patient_id: string | null;
  clinician_id: string | null;
  service_name: string | null;
  starts_at: string;
  ends_at: string;
  service: { is_blocker: boolean } | { is_blocker: boolean }[] | null;
};

function serviceIsBlocker(service: OccupantRow['service']): boolean {
  if (!service) return false;
  if (Array.isArray(service)) return Boolean(service[0]?.is_blocker);
  return Boolean(service.is_blocker);
}

export function useSlotOccupants(input: {
  clinicId: string | null | undefined;
  clinicianId: string | null;
  patientId: string | null;
  start: Date | null;
  enabled: boolean;
}) {
  const { clinicId, clinicianId, patientId, start, enabled } = input;
  const dayStart = start ? new Date(start) : null;
  if (dayStart) dayStart.setHours(0, 0, 0, 0);
  const dayEnd = dayStart ? new Date(dayStart) : null;
  if (dayEnd) dayEnd.setDate(dayEnd.getDate() + 1);
  const dayStartIso = safeIsoString(dayStart);
  const dayEndIso = safeIsoString(dayEnd);
  const clinicianOk = clinicianId != null && UUID.test(clinicianId);
  const patientOk = patientId != null && UUID.test(patientId);

  return useQuery({
    queryKey: [
      'appointments',
      'slot-occupants',
      clinicId ?? null,
      clinicianOk ? clinicianId : null,
      patientOk ? patientId : null,
      dayStartIso,
      dayEndIso,
    ] as const,
    enabled: Boolean(enabled && clinicId && clinicianOk && dayStartIso && dayEndIso),
    queryFn: async (): Promise<SlotOccupant[]> => {
      if (!clinicId || !clinicianOk || !clinicianId || !dayStartIso || !dayEndIso) return [];
      let query = supabase
        .from('appointments')
        .select(
          'id, patient_id, clinician_id, service_name, starts_at, ends_at, service:services!appointments_service_id_fkey(is_blocker)',
        )
        .eq('clinic_id', clinicId)
        .eq('status', 'upcoming')
        .lt('starts_at', dayEndIso)
        .gt('ends_at', dayStartIso);
      query =
        patientOk && patientId
          ? query.or(`clinician_id.eq.${clinicianId},patient_id.eq.${patientId}`)
          : query.eq('clinician_id', clinicianId);
      const { data, error } = await query;
      if (error) throw error;
      return ((data ?? []) as unknown as OccupantRow[]).map((row) => ({
        id: row.id,
        isBlock: serviceIsBlocker(row.service),
        label: row.service_name?.trim() || 'Aftale',
        startsAt: new Date(row.starts_at),
        endsAt: new Date(row.ends_at),
        patientId: row.patient_id,
        clinicianId: row.clinician_id,
      }));
    },
  });
}

type BlockOccupantRow = OccupantRow & {
  patient: { first_name: string | null; last_name: string | null } | { first_name: string | null; last_name: string | null }[] | null;
};

function patientLabel(patient: BlockOccupantRow['patient']): string {
  const row = Array.isArray(patient) ? patient[0] : patient;
  return `${row?.first_name ?? ''} ${row?.last_name ?? ''}`.trim();
}

/** Upcoming appointments and blocks for every selected practitioner that day. */
export function useBlockClinicianOccupants(input: {
  clinicId: string | null | undefined;
  clinicianIds: readonly string[];
  start: Date | null;
  enabled: boolean;
}) {
  const { clinicId, clinicianIds, start, enabled } = input;
  const ids = [...new Set(clinicianIds.filter((id) => UUID.test(id)))].sort();
  const dayStart = start ? clinicStartOfDay(start) : null;
  const dayEnd = start ? clinicAddDays(start, 1) : null;
  const dayStartIso = safeIsoString(dayStart);
  const dayEndIso = safeIsoString(dayEnd);

  return useQuery({
    queryKey: ['appointments', 'block-occupants', clinicId ?? null, ids.join(','), dayStartIso, dayEndIso] as const,
    enabled: Boolean(enabled && clinicId && ids.length > 0 && dayStartIso && dayEndIso),
    queryFn: async (): Promise<BlockTargetOccupant[]> => {
      if (!clinicId || ids.length === 0 || !dayStartIso || !dayEndIso) return [];
      const { data, error } = await supabase
        .from('appointments')
        .select(
          'id, patient_id, clinician_id, service_name, starts_at, ends_at, patient:patients!appointments_patient_id_fkey(first_name,last_name), service:services!appointments_service_id_fkey(is_blocker)',
        )
        .eq('clinic_id', clinicId)
        .eq('status', 'upcoming')
        .in('clinician_id', ids)
        .lt('starts_at', dayEndIso)
        .gt('ends_at', dayStartIso);
      if (error) throw error;
      return ((data ?? []) as unknown as BlockOccupantRow[]).map((row) => {
        const isBlock = serviceIsBlocker(row.service);
        const person = patientLabel(row.patient);
        return {
          id: row.id,
          clinicianId: row.clinician_id,
          isBlock,
          label: isBlock ? row.service_name?.trim() || 'Blokering' : person || row.service_name?.trim() || 'Aftale',
          startsAt: new Date(row.starts_at),
          endsAt: new Date(row.ends_at),
        };
      });
    },
  });
}
